// Quotes › Price lists, one book (Ogma Quote; docs/specs/CONNECT.md section 7.1, docs/specs/QUOTE.md; step 9.16): the
// book's versions. The owner edits a draft (what is sold and how it is billed, volume bands, the questions a price
// needs, the contract term, options, extras, guardrails, and in the advanced part rate tables, pricing steps and refer
// and decline rules), tries it in the test panel against sample cases and their own answers, and publishes it. The test
// panel prices with the very evaluator the "quote" Edge Function uses (lib/quotes.ts), so it shows exactly what a
// customer would be quoted. A price list can be uploaded for Ogma to draft a version from, for the owner to check.
import { useEffect, useId, useMemo, useState, type ReactNode } from "react";
import { useCrm } from "../../lib/session";
import { useAsync } from "../../lib/useAsync";
import type { Crm } from "../../lib/crm";
import { fmtDate, plural } from "../../lib/format";
import {
  answersFrom,
  BILLING_LABEL,
  BILLINGS,
  blankDefinition,
  checkDefinition,
  evaluate,
  formatMoney,
  keyFrom,
  listFrom,
  OUTCOME_LABEL,
  priceLine,
  QUESTION_TYPES,
  relabelPriceItem,
  showAnswer,
  TIERS,
  TIER_LABEL,
  type Answers,
  type Bands,
  type Billing,
  type Condition,
  type FieldValue,
  type OptionDef,
  type PriceDefinition,
  type PriceVersion,
  type Product,
  type Question,
  type QuoteResult,
  type Tier,
} from "../../lib/quotes";
import { Async, Empty, Notice } from "../../components/ui";
import { Card } from "../../components/kit";
import "../settings/settings.css";
import "./pricing.css";
import "../screens-a.css";

const PERIOD: Record<Billing, string> = { one_off: "", monthly: " a month", yearly: " a year" };
const recurring = (o: Pick<QuoteResult["options"][number], "billing">) => o.billing.length > 1 || o.billing.some((b) => b.billing !== "one_off");

// ------------------------------------------------------------------ the price, as a customer would see it, and why
export function OutcomeChip({ outcome }: { outcome: string }) {
  const tone = outcome === "priced" ? "chip-ok" : outcome === "needs_answers" ? "chip-bad" : "chip-warn";
  return <span className={`chip ${tone}`}>{OUTCOME_LABEL[outcome] ?? outcome}</span>;
}

/** A priced result: each option with its lines, steps, totals (per billing type when it is billed more than once),
 *  margin and guardrails; and what sends it to you. */
export function PriceResult({ result, showWorking = true }: { result: QuoteResult; showWorking?: boolean }) {
  const cur = result.currency;
  return (
    <div>
      <div className="price-outcome">
        <OutcomeChip outcome={result.outcome} />
        {result.outcome !== "needs_answers" && (
          <span className={`chip ${result.guardrails_ok ? "chip-ok" : "chip-warn"}`}>{result.guardrails_ok ? "Every guardrail met" : "A guardrail is not met"}</span>
        )}
        {result.term_months !== null && result.term_months !== undefined && <span className="chip chip-plain">Term {result.term_months} months</span>}
      </div>
      {result.issues.length > 0 && (
        <ul className="price-problems plain-list" aria-label="What stops it being priced">
          {result.issues.map((i) => (
            <li key={i}>{i}</li>
          ))}
        </ul>
      )}
      {result.decline.length > 0 && <p>Decline rule: {result.decline.join("; ")}</p>}
      {result.refer.length > 0 && <p>Goes to you: {result.refer.join("; ")}</p>}
      {result.options.length > 0 && (
        <div className="price-options">
          {result.options.map((o) => (
            <section key={o.key} className={`price-option${o.key === result.default_option ? " is-default" : ""}`} aria-label={o.label}>
              {o.tier && TIER_LABEL[o.tier] !== o.label && <span className="muted small">{TIER_LABEL[o.tier]}</span>}
              <h4>
                {o.label} {o.key === result.default_option && <span className="chip chip-plain">Shown first</span>}
              </h4>
              <div className="price-total">{recurring(o) ? priceLine(o.billing, cur) : formatMoney(o.total, cur)}</div>
              {recurring(o) && <p className="muted small">Before tax: {priceLine(o.billing, cur, "net")}</p>}
              <table className="price-lines">
                <tbody>
                  {o.lines.map((l, i) => (
                    <tr key={`l${i}`}>
                      <td>
                        {l.label}
                        {l.quantity !== 1 ? ` × ${l.quantity}` : ""}
                      </td>
                      <td className="num">
                        {formatMoney(l.amount, cur)}
                        {PERIOD[l.billing]}
                      </td>
                    </tr>
                  ))}
                  {showWorking &&
                    o.steps.map((s, i) => (
                      <tr key={`s${i}`} className="muted">
                        <td>{s.label}</td>
                        <td className="num">
                          {s.change >= 0 ? "+" : "−"}
                          {formatMoney(Math.abs(s.change), cur)}
                          {PERIOD[s.billing]}
                        </td>
                      </tr>
                    ))}
                  {!recurring(o) && (
                    <tr>
                      <td>Before tax</td>
                      <td className="num">{formatMoney(o.net, cur)}</td>
                    </tr>
                  )}
                  {o.contract_value !== null && (
                    <tr>
                      <td>Over the whole term</td>
                      <td className="num">{formatMoney(o.contract_value, cur)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
              {showWorking && (
                <p className="muted small">
                  {o.margin_pct !== null ? `Margin ${o.margin_pct}%` : "No costs set"}
                  {o.discount_pct ? ` · Discount ${o.discount_pct}%` : ""}
                </p>
              )}
              {showWorking &&
                o.guardrails.map((g) => (
                  <p key={g.key} className="small">
                    <span className={`chip ${g.ok ? "chip-ok" : "chip-warn"}`}>{g.ok ? "Met" : "Not met"}</span> {g.label}: {g.detail}
                  </p>
                ))}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ answers for a price book's (or a flow's) questions
/** The parts of a question a form shows: a price book's and a question flow's both fit. */
export interface AskedQuestion {
  key: string;
  label: string;
  type: string;
  help?: string;
  show_if?: Condition;
  required?: boolean;
  choices?: { value: string; label?: string }[];
  fields?: AskedQuestion[];
  max_items?: number | null;
}

const MAX_ITEMS = 20;

/** One box for one question: a choice list, yes or no, a date, ticks for a multiple choice, a long box, or text. */
function AnswerBox({ q, id, value, onChange }: { q: AskedQuestion; id: string; value: FieldValue | undefined; onChange: (v: FieldValue) => void }) {
  const text = typeof value === "string" ? value : "";
  if (q.type === "choice") {
    return (
      <select id={id} value={text} onChange={(e) => onChange(e.target.value)}>
        <option value="">Choose…</option>
        {(q.choices ?? []).map((c) => (
          <option key={c.value} value={c.value}>
            {c.label || c.value}
          </option>
        ))}
      </select>
    );
  }
  if (q.type === "yes_no" || q.type === "consent") {
    return (
      <select id={id} value={text} onChange={(e) => onChange(e.target.value)}>
        <option value="">Choose…</option>
        <option value="yes">{q.type === "consent" ? "I agree" : "Yes"}</option>
        <option value="no">{q.type === "consent" ? "I don't agree" : "No"}</option>
      </select>
    );
  }
  if (q.type === "multi_choice") {
    const picked = Array.isArray(value) ? value.filter((x): x is string => typeof x === "string") : [];
    return (
      <span id={id} className="answer-picks">
        {(q.choices ?? []).map((c) => (
          <label key={c.value} className="check">
            <input
              type="checkbox"
              checked={picked.includes(c.value)}
              onChange={(e) => onChange(e.target.checked ? [...picked, c.value] : picked.filter((x) => x !== c.value))}
            />{" "}
            {c.label || c.value}
          </label>
        ))}
      </span>
    );
  }
  if (q.type === "long_text") return <textarea id={id} rows={3} value={text} onChange={(e) => onChange(e.target.value)} />;
  const kind = q.type === "date" ? "date" : q.type === "email" ? "email" : q.type === "phone" ? "tel" : "text";
  const numeric = q.type === "number" || q.type === "money";
  return <input id={id} type={kind} inputMode={numeric ? "decimal" : undefined} value={text} onChange={(e) => onChange(e.target.value)} />;
}

/**
 * A form for a price book's or a flow's questions. A repeating group shows each item's fields, with Add and Remove.
 * `shown` (a flow's show-if rules) decides which questions and group fields appear; `sources` says where an answer
 * was read from.
 */
export function AnswerFields({
  questions,
  values,
  onChange,
  sources,
  shown,
}: {
  questions: AskedQuestion[];
  values: Record<string, FieldValue>;
  onChange: (v: Record<string, FieldValue>) => void;
  sources?: Record<string, string>;
  shown?: (q: AskedQuestion, item?: Record<string, string>) => boolean;
}) {
  const base = useId();
  return (
    <div className="form-grid">
      {questions.map((q) => {
        if (shown && !shown(q)) return null;
        const id = `${base}-${q.key}`;
        const set = (v: FieldValue) => onChange({ ...values, [q.key]: v });
        const hint = [q.help, sources?.[q.key] ? `Read from: ${sources[q.key]}` : null, q.required === false ? "Optional" : null].filter(Boolean).join(" · ");
        if (q.type === "info") {
          return (
            <div key={q.key} className="field answer-info">
              <strong>{q.label}</strong>
              {q.help && <p className="small">{q.help}</p>}
            </div>
          );
        }
        if (q.type === "group") {
          const raw = values[q.key];
          const items = Array.isArray(raw) ? raw.filter((x): x is Record<string, string> => typeof x === "object") : [];
          const most = q.max_items ?? MAX_ITEMS;
          return (
            <fieldset key={q.key} className="answer-group">
              <legend className="field-label">{q.label}</legend>
              {hint && <p className="field-hint">{hint}</p>}
              {items.map((item, i) => (
                <div key={i} className="answer-item" role="group" aria-label={`${q.label} ${i + 1}`}>
                  <div className="answer-item-head">
                    <strong>
                      {q.label} {i + 1}
                    </strong>
                    <button type="button" className="btn btn-small btn-quiet" onClick={() => set(items.filter((_, j) => j !== i))} aria-label={`Remove ${q.label} ${i + 1}`}>
                      Remove
                    </button>
                  </div>
                  <div className="form-grid">
                    {(q.fields ?? []).map((f) => {
                      if (shown && !shown(f, item)) return null;
                      const fid = `${id}-${i}-${f.key}`;
                      return (
                        <label key={f.key} className="field" htmlFor={fid}>
                          <span className="field-label">{f.label}</span>
                          <AnswerBox q={f} id={fid} value={item[f.key] ?? ""} onChange={(v) => set(items.map((x, j) => (j === i ? { ...x, [f.key]: typeof v === "string" ? v : "" } : x)))} />
                          {f.required === false && <span className="field-hint">Optional</span>}
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
              {items.length < most && (
                <button type="button" className="btn btn-small" onClick={() => set([...items, {}])}>
                  Add {items.length ? "another" : "one"}
                </button>
              )}
            </fieldset>
          );
        }
        return (
          <label key={q.key} className="field" htmlFor={id}>
            <span className="field-label">{q.label}</span>
            <AnswerBox q={q} id={id} value={values[q.key]} onChange={set} />
            {hint && <span className="field-hint">{hint}</span>}
          </label>
        );
      })}
    </div>
  );
}

/** Answers as the form's boxes hold them. */
export function answerText(answers: Answers | undefined): Record<string, FieldValue> {
  return Object.fromEntries(Object.entries(answers ?? {}).map(([k, v]) => [k, showAnswer(v)]));
}

// ------------------------------------------------------------------ the test panel
export function TestPanel({ def, defaultOpen }: { def: PriceDefinition; defaultOpen?: boolean }) {
  const [text, setText] = useState<Record<string, FieldValue>>(() => answerText(def.samples?.[0]?.answers));
  const [extras, setExtras] = useState<Record<string, boolean>>({});
  const id = useId();
  const answers = answersFrom(def.questions ?? [], text);
  const result = useMemo(() => evaluate(def, answers, { extras }), [def, JSON.stringify(answers), JSON.stringify(extras)]);
  const samples = def.samples ?? [];
  const runs = useMemo(() => samples.map((s) => ({ s, r: evaluate(def, s.answers) })), [def]);
  const priced = runs.filter(({ r }) => r.outcome === "priced").length;
  return (
    <Card
      id="test-panel"
      level={2}
      className="settings-form"
      title="Test panel"
      subtitle={runs.length ? `${runs.length} sample ${plural(runs.length, "case")}, ${priced} priced` : "No sample cases yet: try your own answers"}
      defaultOpen={defaultOpen}
    >
      <p className="muted small">Try the price list before you publish it. The price here is worked out exactly as a customer's would be.</p>
      {samples.length > 0 && (
        <label className="field" htmlFor={`${id}-s`}>
          <span className="field-label">Start from a sample case</span>
          <select id={`${id}-s`} onChange={(e) => setText(answerText(samples[Number(e.target.value)]?.answers))} defaultValue="0">
            {samples.map((s, i) => (
              <option key={i} value={i}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
      )}
      <AnswerFields questions={def.questions ?? []} values={text} onChange={setText} />
      {(def.extras ?? []).length > 0 && (
        <fieldset>
          <legend className="field-label">Extras the customer switched on</legend>
          {(def.extras ?? []).map((x) => (
            <label key={x.key} className="check">
              <input type="checkbox" checked={extras[x.key] ?? x.on === true} onChange={(e) => setExtras({ ...extras, [x.key]: e.target.checked })} /> {x.label}
            </label>
          ))}
        </fieldset>
      )}
      <PriceResult result={result} />
      {runs.length > 0 && (
        <>
          <h3>Every sample case</h3>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Case</th>
                  <th>Result</th>
                  <th className="num">Shown first</th>
                </tr>
              </thead>
              <tbody>
                {runs.map(({ s, r }, i) => (
                  <tr key={i}>
                    <td>{s.label}</td>
                    <td>
                      <OutcomeChip outcome={r.outcome} /> {!r.guardrails_ok && r.outcome !== "needs_answers" ? <span className="chip chip-warn">Guardrail</span> : null}
                    </td>
                    <td className="num">
                      {(() => {
                        const o = r.options.find((x) => x.key === r.default_option);
                        return o ? (recurring(o) ? priceLine(o.billing, r.currency) : formatMoney(o.total, r.currency)) : "—";
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Card>
  );
}

// ------------------------------------------------------------------ the editor
function Row({ children, onRemove, label }: { children: ReactNode; onRemove?: () => void; label: string }) {
  return (
    <li className="settings-row">
      {children}
      {onRemove && (
        <button type="button" className="btn btn-small btn-quiet" onClick={onRemove} aria-label={`Remove ${label}`}>
          Remove
        </button>
      )}
    </li>
  );
}

export function Box({ label, value, onChange, type = "text", hint }: { label: string; value: string | number | null | undefined; onChange: (v: string) => void; type?: string; hint?: string }) {
  const id = useId();
  return (
    <label className="field" htmlFor={id}>
      <span className="field-label">{label}</span>
      <input id={id} type={type} inputMode={type === "number" ? "decimal" : undefined} value={typeof value === "number" && Number.isNaN(value) ? "" : (value ?? "")} onChange={(e) => onChange(e.target.value)} />
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  );
}

/** A box for a list written as text ("a; b; c"): it keeps what is typed and reads it when the box is left. */
export function ListBox({ label, value, onCommit, hint }: { label: string; value: string; onCommit: (v: string) => void; hint?: string }) {
  const id = useId();
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  return (
    <label className="field" htmlFor={id}>
      <span className="field-label">{label}</span>
      <input id={id} type="text" value={text} onChange={(e) => setText(e.target.value)} onBlur={() => onCommit(text)} />
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  );
}

export const num = (v: string): number => (v.trim() === "" ? NaN : Number(v));
export const optNum = (v: string): number | null => (v.trim() === "" ? null : Number(v));

/** Volume bands as words: "10: 45; 50: 40; *: 35" (up to 10 at £45, up to 50 at £40, then £35). */
export function bandsText(b: Bands | undefined): string {
  return (b?.rows ?? []).map((r) => `${r.up_to === null ? "*" : r.up_to}: ${r.price}`).join("; ");
}

/** Volume bands from their words; empty words: no bands. */
export function bandsFrom(text: string, mode: Bands["mode"]): Bands | undefined {
  const rows = text
    .split(";")
    .map((x) => x.trim())
    .filter(Boolean)
    .map((x) => {
      const [upTo = "", price = ""] = x.split(":").map((y) => y.trim());
      return { up_to: upTo === "*" || upTo === "" ? null : Number(upTo), price: Number(price) };
    });
  return rows.length ? { mode, rows } : undefined;
}

/** An option's lines: each a product picked from the book's list and its quantity (a number, or a sum on the answers);
 * a line's condition stays with it. */
function LinesEditor({ lines, products, names, onChange }: { lines: OptionDef["lines"]; products: Product[]; names: string; onChange: (lines: OptionDef["lines"]) => void }) {
  const set = (i: number, patch: Partial<OptionDef["lines"][number]>) => onChange(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  return (
    <fieldset className="choice-list option-lines">
      <legend className="field-label">Lines</legend>
      {lines.map((l, i) => (
        <div key={i} className="inline wrap" role="group" aria-label={`Line ${i + 1}`}>
          <label className="field">
            <span className="field-label">Product</span>
            <select value={l.product} onChange={(e) => set(i, { product: e.target.value })}>
              {!products.some((p) => p.key === l.product) && <option value={l.product}>Not in your list</option>}
              {products.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
          <Box label="Quantity" value={l.quantity} onChange={(v) => set(i, { quantity: v })} />
          {l.when && <span className="muted small">Only when its condition holds (in the advanced part).</span>}
          <button type="button" className="btn btn-small btn-quiet" onClick={() => onChange(lines.filter((_, j) => j !== i))} aria-label={`Remove line ${i + 1}`}>
            Remove
          </button>
        </div>
      ))}
      {products.length > 0 && (
        <button type="button" className="btn btn-small" onClick={() => onChange([...lines, { product: products[0]!.key, quantity: "1" }])}>
          Add a line
        </button>
      )}
      {names && <span className="field-hint">A quantity is a number or a sum on your number questions. Name them in it as: {names}.</span>}
    </fieldset>
  );
}

/** A group's fields as words ("Users: number; Servers: number"); a field keeps its range while its key does. */
function fieldsFrom(text: string, before: Question[]): Question[] {
  const taken: string[] = [];
  return listFrom(text.replace(/;/g, ",")).map((x) => {
    const [label = "", type = "number"] = x.split(":").map((y) => y.trim());
    const key = before.find((f) => f.label === label)?.key ?? keyFrom(label, taken);
    taken.push(key);
    const t = (QUESTION_TYPES as readonly string[]).includes(type) && type !== "group" ? (type as Question["type"]) : "number";
    const old = before.find((f) => f.key === key);
    return { ...(old ?? {}), key, label, type: t };
  });
}

/** The parts edited as JSON: rate tables, pricing steps, refer and decline rules, sample cases. */
export function advancedOf(d: PriceDefinition) {
  return { tables: d.tables ?? [], steps: d.steps ?? [], refer: d.refer ?? [], decline: d.decline ?? [], samples: d.samples ?? [] };
}

export function PriceEditor({ crm, version, published, onSaved }: { crm: Crm; version: PriceVersion; published: PriceDefinition | null; onSaved: () => void }) {
  const [def, setDef] = useState<PriceDefinition>(() => structuredClone(version.definition));
  const [json, setJson] = useState(() => JSON.stringify(advancedOf(version.definition), null, 2));
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ text: string; tone: "ok" | "bad" } | null>(null);
  useEffect(() => {
    setDef(structuredClone(version.definition));
    setJson(JSON.stringify(advancedOf(version.definition), null, 2));
  }, [version.id]);
  const problems = useMemo(() => checkDefinition(def), [def]);
  const set = (patch: Partial<PriceDefinition>) => setDef({ ...def, ...patch });
  const products = def.products ?? [];
  const questions = def.questions ?? [];
  const productKeys = products.map((p) => p.key);
  const numberQuestions = questions.filter((q) => q.type === "number");
  // Keys the published version uses stay as they are (quotes and question flows depend on them); others follow names.
  const fixed = useMemo(() => new Set([...(published?.products ?? []).map((p) => p.key), ...(published?.questions ?? []).map((q) => q.key)]), [published]);
  const names = questions.filter((q) => q.type === "number" || q.type === "group").map((q) => `${q.label} = ${q.type === "group" ? `sum(${q.key}.<field>) or count(${q.key})` : q.key}`).join("; ");

  const applyJson = (text: string) => {
    setJson(text);
    try {
      const a = JSON.parse(text) as ReturnType<typeof advancedOf>;
      if (!a || typeof a !== "object" || Array.isArray(a)) throw new Error("must be an object");
      setDef((d) => ({ ...d, tables: a.tables ?? [], steps: a.steps ?? [], refer: a.refer ?? [], decline: a.decline ?? [], samples: a.samples ?? [] }));
      setJsonError(null);
    } catch (e) {
      setJsonError(`Not saved until it reads as JSON: ${(e as Error).message}`);
    }
  };

  const run = async (what: "save" | "publish" | "discard") => {
    setBusy(true);
    setMsg(null);
    try {
      if (what === "discard") {
        await crm.discardPriceDraft(version.id);
        onSaved();
        return;
      }
      const saved = await crm.savePriceDraft(def, { version: version.id });
      if (what === "publish") {
        const p = await crm.publishPriceVersion(version.id);
        setMsg({ text: `Version ${p.version} is published. New quotes use it; Ogma shows you its first quotes before they go.`, tone: "ok" });
      } else {
        setMsg({ text: saved.problems.length ? `Saved. ${saved.problems.length} thing${saved.problems.length === 1 ? "" : "s"} to fix before you can publish.` : "Saved. It's ready to publish.", tone: "ok" });
      }
      onSaved();
    } catch (e) {
      setMsg({ text: (e as Error).message, tone: "bad" });
    } finally {
      setBusy(false);
    }
  };

  const upProduct = (i: number, patch: Partial<Product>) => set({ products: products.map((x, j) => (j === i ? { ...x, ...patch } : x)) });

  return (
    <>
      <Card
        id={`draft-${version.id}`}
        persist={false}
        level={2}
        className="settings-form"
        title={`Draft version ${version.version}`}
        subtitle={`${problems.length ? `${problems.length} to fix before publishing` : "Ready to publish"} · ${version.source === "upload" ? "drafted from your price list" : `last changed ${fmtDate(version.updated_at)}`}`}
        defaultOpen
      >
        {version.note && <p className="muted small">{version.note}</p>}

        <h3>The basics</h3>
        <div className="form-grid">
          <Box label="Currency" value={def.currency} onChange={(v) => set({ currency: v.toUpperCase().slice(0, 3) })} hint="Three letters, such as GBP" />
          <Box label="Quotes are valid for (days)" type="number" value={def.guardrails?.validity_days} onChange={(v) => set({ guardrails: { ...def.guardrails, validity_days: num(v) } })} />
          <Box label="Lowest margin (%)" type="number" value={def.guardrails?.min_margin_pct ?? ""} onChange={(v) => set({ guardrails: { ...def.guardrails, min_margin_pct: optNum(v) } })} hint="Below it, a quote waits for you. Needs costs on your products." />
          <Box label="Largest discount (%)" type="number" value={def.guardrails?.max_discount_pct ?? ""} onChange={(v) => set({ guardrails: { ...def.guardrails, max_discount_pct: optNum(v) } })} hint="Above it, a quote waits for you." />
          <label className="field">
            <span className="field-label">Contract term</span>
            <select
              value={def.term?.question ?? ""}
              onChange={(e) => {
                const { term: _old, ...rest } = def;
                setDef(e.target.value ? { ...rest, term: { question: e.target.value } } : rest);
              }}
            >
              <option value="">No term</option>
              {numberQuestions.map((q) => (
                <option key={q.key} value={q.key}>
                  {q.label} (months)
                </option>
              ))}
            </select>
            <span className="field-hint">The number question that holds the term in months. It is shown on the quote, with the value over the whole term.</span>
          </label>
        </div>
        <label className="field">
          <span className="field-label">Terms shown on the quote (optional)</span>
          <textarea rows={2} maxLength={2000} value={def.terms ?? ""} onChange={(e) => set({ terms: e.target.value })} />
        </label>

        <h3>What you sell</h3>
        <ul className="settings-rows">
          {products.map((p, i) => (
            <Row key={i} label={p.label} onRemove={() => set({ products: products.filter((_, j) => j !== i) })}>
              <Box label="Name" value={p.label} onChange={(v) => setDef((d) => relabelPriceItem(d, "products", i, v, fixed))} />
              <Box label="Unit" value={p.unit ?? ""} onChange={(v) => upProduct(i, { unit: v })} />
              <label className="field">
                <span className="field-label">Billed</span>
                <select value={p.billing ?? "one_off"} onChange={(e) => upProduct(i, { billing: e.target.value as Billing })}>
                  {BILLINGS.map((b) => (
                    <option key={b} value={b}>
                      {BILLING_LABEL[b]}
                    </option>
                  ))}
                </select>
              </label>
              {!p.bands && <Box label="Price" type="number" value={p.price} onChange={(v) => upProduct(i, { price: num(v) })} />}
              <ListBox
                label="Volume bands (optional)"
                value={bandsText(p.bands)}
                onCommit={(v) => {
                  const bands = bandsFrom(v, p.bands?.mode ?? "all_units");
                  upProduct(i, bands ? { bands, price: undefined } : { bands: undefined, price: p.price ?? 0 });
                }}
                hint="Up to: price, such as 10: 45; 50: 40; *: 35. Leave empty for one price."
              />
              {p.bands && (
                <label className="field">
                  <span className="field-label">Bands price</span>
                  <select value={p.bands.mode} onChange={(e) => upProduct(i, { bands: { ...p.bands!, mode: e.target.value as Bands["mode"] } })}>
                    <option value="all_units">Every unit at the band the total falls in</option>
                    <option value="graduated">Each band's units at that band's price</option>
                  </select>
                </label>
              )}
              <Box label="Cost (optional)" type="number" value={p.cost ?? ""} onChange={(v) => upProduct(i, { cost: optNum(v) })} />
            </Row>
          ))}
        </ul>
        <button type="button" className="btn btn-small" onClick={() => set({ products: [...products, { key: keyFrom("New item", productKeys), label: "New item", unit: "item", price: 0 }] })}>
          Add a product or service
        </button>

        <h3>The questions a price needs</h3>
        <ul className="settings-rows">
          {questions.map((q, i) => {
            const up = (patch: Partial<Question>) => set({ questions: questions.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
            return (
              <Row key={i} label={q.label} onRemove={() => set({ questions: questions.filter((_, j) => j !== i) })}>
                <Box label="Question" value={q.label} onChange={(v) => setDef((d) => relabelPriceItem(d, "questions", i, v, fixed))} />
                <label className="field">
                  <span className="field-label">Type</span>
                  <select value={q.type} onChange={(e) => up({ type: e.target.value as Question["type"] })}>
                    {QUESTION_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t === "yes_no" ? "yes or no" : t === "group" ? "repeating group" : t}
                      </option>
                    ))}
                  </select>
                </label>
                {q.type === "number" && <Box label="Lowest" type="number" value={q.min ?? ""} onChange={(v) => up({ min: optNum(v) })} />}
                {q.type === "number" && <Box label="Highest" type="number" value={q.max ?? ""} onChange={(v) => up({ max: optNum(v) })} />}
                {q.type === "choice" && (
                  <ListBox label="Choices" value={(q.choices ?? []).map((c) => c.value).join(", ")} onCommit={(v) => up({ choices: listFrom(v).map((value) => ({ value })) })} hint="Separated by commas" />
                )}
                {q.type === "group" && (
                  <ListBox
                    label="Fields (name: type)"
                    value={(q.fields ?? []).map((f) => `${f.label}: ${f.type}`).join("; ")}
                    onCommit={(v) => up({ fields: fieldsFrom(v, q.fields ?? []) })}
                    hint={`Such as Users: number; Servers: number. Add them up in a quantity with sum(${q.key}.users), count them with count(${q.key}).`}
                  />
                )}
                <label className="check">
                  <input type="checkbox" checked={q.required !== false} onChange={(e) => up({ required: e.target.checked })} /> Needed
                </label>
              </Row>
            );
          })}
        </ul>
        <button type="button" className="btn btn-small" onClick={() => set({ questions: [...questions, { key: keyFrom("New question", questions.map((q) => q.key)), label: "New question", type: "number", min: 0 }] })}>
          Add a question
        </button>

        <h3>Options</h3>
        <p className="muted small">
          Up to five, each with its own name; good, better and best are optional tiers. Each option is a set of lines. A quantity is a number or a sum on your number questions, such as “sites” or
          “rooms * 2”, or over a group, “sum(sites.users)”.
        </p>
        <ul className="settings-rows">
          {def.options.map((o, i) => {
            const up = (patch: Partial<OptionDef>) => set({ options: def.options.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
            return (
              <Row key={i} label={o.label} onRemove={def.options.length > 1 ? () => set({ options: def.options.filter((_, j) => j !== i) }) : undefined}>
                <Box label="Option" value={o.label} onChange={(v) => up({ label: v })} />
                <label className="field">
                  <span className="field-label">Tier</span>
                  <select
                    value={o.tier ?? ""}
                    onChange={(e) => {
                      const { tier: _t, ...rest } = o;
                      set({ options: def.options.map((x, j) => (j === i ? (e.target.value ? { ...rest, tier: e.target.value as Tier } : rest) : x)) });
                    }}
                  >
                    <option value="">No tier</option>
                    {TIERS.map((t) => (
                      <option key={t} value={t}>
                        {TIER_LABEL[t]}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="check">
                  <input type="radio" name="default-option" checked={o.default === true} onChange={() => set({ options: def.options.map((x, j) => ({ ...x, default: j === i })) })} /> Shown first
                </label>
                <LinesEditor lines={o.lines} products={products} names={names} onChange={(lines) => up({ lines })} />
                <ListBox label="What is included" value={(o.included ?? []).join("; ")} onCommit={(v) => up({ included: v.split(";").map((x) => x.trim()).filter(Boolean) })} hint="Separated by ;" />
              </Row>
            );
          })}
        </ul>
        {def.options.length < 5 && (
          <button
            type="button"
            className="btn btn-small"
            onClick={() => {
              // A name of its own ("Option 2"), so two options never look alike; a tier is chosen on the option.
              const labels = def.options.map((o) => o.label);
              let n = def.options.length + 1;
              while (labels.includes(`Option ${n}`)) n += 1;
              const label = `Option ${n}`;
              const lines = products[0] ? [{ product: products[0].key, quantity: "1" }] : [];
              set({ options: [...def.options, { key: keyFrom(label, def.options.map((o) => o.key)), label, lines, included: [] }] });
            }}
          >
            Add an option
          </button>
        )}

        <h3>Extras the customer can add</h3>
        <ul className="settings-rows">
          {(def.extras ?? []).map((x, i) => {
            const extras = def.extras ?? [];
            const up = (patch: Partial<typeof x>) => set({ extras: extras.map((y, j) => (j === i ? { ...y, ...patch } : y)) });
            return (
              <Row key={i} label={x.label} onRemove={() => set({ extras: extras.filter((_, j) => j !== i) })}>
                <Box label="Extra" value={x.label} onChange={(v) => up({ label: v })} />
                <label className="field">
                  <span className="field-label">Product</span>
                  <select value={x.product} onChange={(e) => up({ product: e.target.value })}>
                    {products.map((p) => (
                      <option key={p.key} value={p.key}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </label>
                <Box label="Quantity" value={x.quantity ?? "1"} onChange={(v) => up({ quantity: v })} />
                <label className="check">
                  <input type="checkbox" checked={x.on === true} onChange={(e) => up({ on: e.target.checked })} /> On unless they switch it off
                </label>
              </Row>
            );
          })}
        </ul>
        {products.length > 0 && (
          <button type="button" className="btn btn-small" onClick={() => set({ extras: [...(def.extras ?? []), { key: keyFrom("Extra", [...(def.extras ?? []).map((x) => x.key), ...def.options.map((o) => o.key)]), label: "Extra", product: products[0]!.key, quantity: "1", on: false }] })}>
            Add an extra
          </button>
        )}

        {/* card-check: not a card (the advanced part of the draft, folded inside the draft's card) */}
        <details className="disclosure">
          <summary>Rate tables, pricing steps, refer and decline rules, sample cases</summary>
          <p className="muted small">
            Pricing steps run in order: factors from rate tables and adjustments (plain sums on the answers), then minimums, then (with the extras) discounts, then tax. A step can name the
            billing it applies to ({"\""}billing{"\""}: {"\""}monthly{"\""}); one that adds a fixed amount must, when you bill more than one way. Refer rules send a quote to you, for example{" "}
            {"{"}"reason": "Over £20,000", "when": {"{"}"q": "total", "op": "gt", "value": 20000{"}"}{"}"}.
          </p>
          <textarea className="price-json" aria-label="Rate tables, pricing steps, rules and samples (JSON)" value={json} onChange={(e) => applyJson(e.target.value)} spellCheck={false} />
          <Notice text={jsonError} tone="bad" />
        </details>

        {problems.length > 0 ? (
          <div className="price-problems" role="status">
            <strong>To fix before publishing ({problems.length})</strong>
            <ul className="plain-list">
              {problems.slice(0, 12).map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="small">
            <span className="chip chip-ok">Ready to publish</span> Ogma will show you the first quotes from it before they go.
          </p>
        )}
        <Notice text={msg?.text ?? null} tone={msg?.tone} />
        <div className="settings-foot">
          <button type="button" className="btn" disabled={busy || jsonError !== null} onClick={() => void run("save")}>
            Save the draft
          </button>
          <button type="button" className="btn btn-primary" disabled={busy || jsonError !== null || problems.length > 0} onClick={() => void run("publish")}>
            Publish this version
          </button>
          <button type="button" className="btn btn-quiet" disabled={busy} onClick={() => void run("discard")}>
            Throw the draft away
          </button>
        </div>
      </Card>
      <TestPanel def={def} />
    </>
  );
}

// ------------------------------------------------------------------ uploading a price list
export function UploadPriceList({ crm, reading, onSent, defaultOpen }: { crm: Crm; reading: boolean; onSent: () => void; defaultOpen?: boolean }) {
  const [text, setText] = useState("");
  const [name, setName] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ text: string; tone: "ok" | "bad" } | null>(null);
  const id = useId();
  const send = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await crm.uploadPriceList(text, name);
      setText("");
      setMsg({ text: "Ogma is reading it. A draft version appears in your default price list shortly, for you to check every price before publishing.", tone: "ok" });
      onSent();
    } catch (e) {
      setMsg({ text: (e as Error).message, tone: "bad" });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card
      id="price-list"
      level={2}
      className="settings-form"
      title="Start from your price list"
      subtitle={reading ? "Ogma is reading the last price list you gave it" : "Paste a price list or choose a file for a draft"}
      defaultOpen={defaultOpen}
    >
      <p className="muted small">Paste your price list, or choose a text or CSV file. Ogma drafts a price list from it; nothing is used until you check it and publish it.</p>
      {reading ? (
        <p>
          <span className="chip chip-info">Reading</span> Ogma is reading the last price list you gave it.
        </p>
      ) : (
        <>
          <label className="field" htmlFor={`${id}-f`}>
            <span className="field-label">A file (text or CSV)</span>
            <input
              id={`${id}-f`}
              type="file"
              accept=".txt,.csv,text/plain,text/csv"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                setName(f.name);
                setText((await f.text()).slice(0, 60000));
              }}
            />
          </label>
          <label className="field" htmlFor={`${id}-t`}>
            <span className="field-label">Or paste it</span>
            <textarea id={`${id}-t`} rows={6} value={text} onChange={(e) => setText(e.target.value)} />
          </label>
          <button type="button" className="btn" disabled={busy || text.trim().length < 20} onClick={() => void send()}>
            Draft a price list from it
          </button>
        </>
      )}
      <Notice text={msg?.text ?? null} tone={msg?.tone} />
    </Card>
  );
}

// ------------------------------------------------------------------ one book's versions
export function versionLine(v: PriceVersion, checkFirst: number): string {
  if (v.status === "published") {
    const checked = Math.min(v.quotes, checkFirst);
    return `Published ${fmtDate(v.published_at)} · ${v.quotes} quote${v.quotes === 1 ? "" : "s"}, ${v.accepted} accepted · ${checked < checkFirst ? `you check its first ${checkFirst} (${checked} so far)` : `past its first ${checkFirst}: quotes inside your guardrails go by themselves`}`;
  }
  if (v.status === "retired") return `Retired ${fmtDate(v.retired_at)} · priced ${v.quotes} quote${v.quotes === 1 ? "" : "s"}`;
  return `Draft · changed ${fmtDate(v.updated_at)}${v.source === "upload" ? " · from your price list" : ""}`;
}

/**
 * One price book (the default when `book` is null): its versions, the editor and test panel, and starting from your
 * price list. The quotes waiting for you are on Quotes. `children` (the deposit on acceptance) go at the end.
 */
export function Pricing({ book = null, children }: { book?: string | null; children?: ReactNode }) {
  const crm = useCrm();
  const versions = useAsync(() => crm.priceBook(book), [crm, book]);
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const newDraft = async (bookId: string | null, from?: PriceVersion) => {
    setBusy(true);
    setError(null);
    try {
      const r = await crm.savePriceDraft(from ? structuredClone(from.definition) : blankDefinition(), { note: from ? `A copy of version ${from.version}` : "A new start", book: bookId });
      setOpen(r.id);
      versions.reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pricing-book">
      <Async state={versions}>
        {(b) => {
          const published = b.versions.find((v) => v.status === "published");
          const drafts = b.versions.filter((v) => v.status === "draft");
          const editing = drafts.find((v) => v.id === open) ?? null;
          const bookId = b.book?.id ?? book;
          return (
            <>
              {b.versions.length === 0 ? (
                <Empty title="No versions yet">
                  <p>Start one from scratch or from your price list. Quotes are made from it once you publish it.</p>
                </Empty>
              ) : (
                <Card
                  id={`versions-${bookId ?? "default"}`}
                  level={2}
                  title={b.book ? `${b.book.name}: versions` : "Versions"}
                  subtitle={`${published ? `Version ${published.version} in use` : "Nothing published yet"}${drafts.length ? ` · ${drafts.length} ${plural(drafts.length, "draft")}` : ""}`}
                  defaultOpen
                >
                  <ul className="price-versions">
                    {b.versions.map((v) => (
                      <li key={v.id}>
                        <strong>Version {v.version}</strong>
                        {v.status === "published" && <span className="chip chip-ok">In use</span>}
                        {v.status === "draft" && <span className="chip chip-warn">Draft</span>}
                        <span className="muted small">{versionLine(v, b.check_first)}</span>
                        {v.status === "draft" && (
                          <button type="button" className="btn btn-small" onClick={() => setOpen(open === v.id ? null : v.id)} aria-expanded={open === v.id}>
                            {open === v.id ? "Close" : "Edit and test"}
                          </button>
                        )}
                        {v.status !== "draft" && (
                          <button type="button" className="btn btn-small btn-quiet" disabled={busy} onClick={() => void newDraft(bookId, v)}>
                            Copy into a new draft
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </Card>
              )}
              {!published && b.versions.length > 0 && <p className="small">Nothing is published in this book yet, so Ogma makes no quotes from it. Publish a draft when its test cases look right.</p>}
              <div className="action-bar">
                <button type="button" className="btn" disabled={busy} onClick={() => void newDraft(bookId)}>
                  Start a blank draft
                </button>
              </div>
              <Notice text={error} tone="bad" />
              {editing && <PriceEditor crm={crm} version={editing} published={published?.definition ?? null} onSaved={() => versions.reload()} />}
              {!editing && published && <TestPanel def={published.definition} />}
              <UploadPriceList crm={crm} reading={b.reading} onSent={() => versions.reload()} defaultOpen={b.versions.length === 0} />
            </>
          );
        }}
      </Async>
      {children}
    </div>
  );
}
